import { useState } from 'react';
import { format } from 'date-fns';
import { Eye, Download, Zap, Trash2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  type LegalDocument,
  type LegalDocumentType,
  useLegalDocumentVersions,
  useActivateLegalDocument,
  useDeleteLegalDocument,
} from '@/hooks/useLegalDocuments';
import { LegalDocumentPreviewModal } from './LegalDocumentPreviewModal';
import { Skeleton } from '@/components/ui/skeleton';

interface LegalDocumentVersionHistoryProps {
  documentType: LegalDocumentType;
}

export function LegalDocumentVersionHistory({ documentType }: LegalDocumentVersionHistoryProps) {
  const { data: versions, isLoading } = useLegalDocumentVersions(documentType);
  const activateDocument = useActivateLegalDocument();
  const deleteDocument = useDeleteLegalDocument();

  const [previewDocument, setPreviewDocument] = useState<LegalDocument | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<LegalDocument | null>(null);
  const [activateConfirm, setActivateConfirm] = useState<LegalDocument | null>(null);

  const handleDownload = (doc: LegalDocument) => {
    const blob = new Blob([doc.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.document_type}-v${doc.version}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleActivate = async () => {
    if (!activateConfirm) return;
    await activateDocument.mutateAsync({
      documentId: activateConfirm.id,
      documentType,
    });
    setActivateConfirm(null);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    await deleteDocument.mutateAsync({
      documentId: deleteConfirm.id,
      documentType,
    });
    setDeleteConfirm(null);
  };

  const getStatusBadge = (doc: LegalDocument) => {
    if (doc.is_active) {
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Active</Badge>;
    }
    if (doc.activated_at) {
      return <Badge variant="secondary">Archived</Badge>;
    }
    return <Badge variant="outline" className="text-yellow-600 border-yellow-500/50">Draft</Badge>;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Version History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Version History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!versions || versions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No versions found. Upload a document above to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Effective Date</TableHead>
                  <TableHead>Activated</TableHead>
                  <TableHead>Inactivated</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">{doc.version}</TableCell>
                    <TableCell>{getStatusBadge(doc)}</TableCell>
                    <TableCell>
                      {doc.effective_date
                        ? format(new Date(doc.effective_date + 'T12:00:00'), 'MMM d, yyyy')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {doc.activated_at
                        ? format(new Date(doc.activated_at), 'MMM d, yyyy h:mm a')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {doc.inactivated_at
                        ? format(new Date(doc.inactivated_at), 'MMM d, yyyy h:mm a')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {format(new Date(doc.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setPreviewDocument(doc)}
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownload(doc)}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {!doc.is_active && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setActivateConfirm(doc)}
                              title="Activate"
                              className="text-green-600 hover:text-green-700"
                            >
                              <Zap className="h-4 w-4" />
                            </Button>
                            {!doc.activated_at && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteConfirm(doc)}
                                title="Delete"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <LegalDocumentPreviewModal
        document={previewDocument}
        onClose={() => setPreviewDocument(null)}
      />

      {/* Activate Confirmation */}
      <AlertDialog open={!!activateConfirm} onOpenChange={() => setActivateConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate Document Version</AlertDialogTitle>
            <AlertDialogDescription>
              This will make version {activateConfirm?.version} the active version and deactivate
              the currently active version. The change will be immediately visible to users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleActivate}>Activate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Draft</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete version {deleteConfirm?.version}? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
